import React, { useEffect, useRef } from 'react';
import './styles.css'; // Import the prefixed CSS

// --- Import FontAwesomeIcon Component ---
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// --- Import ALL Necessary Icons from Font Awesome (Updated for Design Review Content) ---
import {
    // Core Concepts & General
    faSearchPlus,       // Design Review / Scrutiny
    faHospital,         // Hospital / Healthcare Facility
    faCheckCircle,      // Compliance / Accreditation / Excellence
    faShieldAlt,        // Safety / Risk Mitigation
    faTasks,            // Operational Efficiency / Workflow
    faUsers,            // Staff / Team
    faHeartPulse,       // Patient Well-being / Care
    // Benefits
    faAward,            // Accreditation ( JCI) / Compliance
    faProjectDiagram,   // Optimized Layout / Flow / Space Planning
    faLeaf,             // Sustainability / Eco-Friendly
    // Review Process Elements
    faDraftingCompass,  // Architectural Design
    faCogs,             // MEP Design
    faPalette,          // Interior Design
    faDirections,       // Wayfinding / Signage
    faRulerCombined,    // Structural Design
    // Advanced Features Reviewed
    faBiohazard,        // Infection Control (or faShieldVirus)
    faMicroscope,       // Equipment Planning / Technology (or faTools)
    faExpandArrowsAlt,  // Expansion / Adaptability / Future-Ready
    // Keep other potentially useful icons if needed
    // Icons likely unused from previous versions:
    /*
    faKey, faCog, faGlobe, faLaptopCode, faTree, faDollarSign, faRocket, faUserCheck
    */
} from '@fortawesome/free-solid-svg-icons'; // Using free solid icons

// --- Intersection Observer Hooks (No changes needed in hook logic) ---
const useIntersectionObserverTitle = (options) => {
    const containerRef = useRef(null);
    const [isVisible, setIsVisible] = React.useState(false);
    const callbackFunction = (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) setIsVisible(true);
    };
    useEffect(() => {
        const observer = new IntersectionObserver(callbackFunction, options);
        const currentRef = containerRef.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef) observer.unobserve(currentRef); };
    }, [containerRef, options]);
    return [containerRef, isVisible];
};

const useIntersectionObserverElements = (options) => {
    useEffect(() => {
        const observerCallback = (entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const target = entry.target;
                    const parent = target.parentElement;
                    const siblings = parent ? Array.from(parent.children).filter(el => el.classList.contains('cliniqor-fade-in-up')) : [target];
                    const index = siblings.indexOf(target);
                    const staggerIndex = index >= 0 ? index : 0;

                    target.style.setProperty('--item-index', staggerIndex % 8); // Keep stagger logic
                    target.classList.add('cliniqor-visible'); // Use prefixed class
                    observer.unobserve(target);
                }
            });
        };
        const observer = new IntersectionObserver(observerCallback, options);
        const targets = document.querySelectorAll('.cliniqor-fade-in-up');
        targets.forEach(target => observer.observe(target));
        return () => targets.forEach(target => { if (target) observer.unobserve(target); });
    }, [options]);
};
// --- End Observer Hooks ---


function App() {

    // --- Observer Setup for Titles (Adjusted Refs) ---
    const titleObserverOptions = { threshold: 0.2 };
    const [title1Ref, title1Visible] = useIntersectionObserverTitle(titleObserverOptions); // Importance of Review
    const [title2Ref, title2Visible] = useIntersectionObserverTitle(titleObserverOptions); // Key Benefits
    const [title3Ref, title3Visible] = useIntersectionObserverTitle(titleObserverOptions); // Review Process (Part 1)
    const [title4Ref, title4Visible] = useIntersectionObserverTitle(titleObserverOptions); // Advanced Features Reviewed
    const [title5Ref, title5Visible] = useIntersectionObserverTitle(titleObserverOptions); // Cliniqor's Commitment

    // --- Observer Setup for General Fade-In Elements ---
    useIntersectionObserverElements({ threshold: 0.1 });

    // Choose a relevant hero background image for review/analysis
    const heroBgImage = 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80'; // Image suggesting analysis/review

  return(
    // Use prefixed base wrapper class
    <div className="cliniqor-app-wrapper">

      {/* --- HERO SECTION --- */}
      <header
        className="cliniqor-hero-section" // Prefixed
        style={{ backgroundImage: `url(${heroBgImage})` }}
      >
        <div className="cliniqor-container"> {/* Prefixed */}
          <div className="cliniqor-hero-content"> {/* Prefixed */}
            {/* --- NEW HERO CONTENT --- */}
            <h1>Comprehensive Hospital Design Review</h1>
            <p className="cliniqor-sub-heading">
              Ensuring Excellence in Healthcare Architecture with Cliniqor
            </p>
            <p className="cliniqor-intro-text">
              Cliniqor offers unparalleled expertise in hospital design review, a critical phase ensuring long-term sustainability, safety, and efficacy in healthcare delivery. We shape environments that enhance well-being, ensure efficiency, and adhere to stringent standards.
            </p>
            {/* --- END NEW HERO CONTENT --- */}
          </div>
        </div>
      </header>

      <main>
        {/* --- IMPORTANCE OF REVIEW --- */}
        <section className="cliniqor-section cliniqor-section-first cliniqor-section-bg-gradient cliniqor-intro-section">
          <div className="cliniqor-container">
            <div ref={title1Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title1Visible ? 'cliniqor-visible' : ''}`}>
              <h2>The Importance of Hospital Design Review</h2>
              <p>Ensuring Excellence, Compliance, and Optimal Functionality</p>
            </div>
             <div className="cliniqor-intro-grid"> {/* Keeping grid structure */}
                 <div className="cliniqor-intro-text-content cliniqor-fade-in-up">
                     {/* --- NEW INTRO CONTENT --- */}
                     <p>A hospital is a complex ecosystem requiring seamless function. The design review process is essential to ensure all aspects, from fundamental safety to details affecting patient care and operational efficiency, are correctly implemented.</p>
                     <p>Cliniqor's review refines the design to facilitate better patient outcomes, enhance staff workflows, and build a sustainable, future-ready infrastructure, going beyond mere regulatory compliance.</p>
                     {/* --- END NEW INTRO CONTENT --- */}
                 </div>
             </div>
          </div>
        </section>

        {/* --- KEY BENEFITS (Features Section) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
          <div className="cliniqor-container">
            <div ref={title2Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title2Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Key Benefits of Cliniqor’s Hospital Design Review Services</h2>
              <p>Achieving compliance, efficiency, and sustainability.</p>
              {/* --- END NEW SECTION TITLE --- */}
            </div>
            <div className="cliniqor-features-grid"> {/* Grid structure maintained (3 items fit well) */}
              {/* --- NEW FEATURE CARDS --- */}
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faAward} /></div>
                    <h4>1. Regulatory & Accreditation Compliance</h4>
                    <p>Ensuring alignment with  JCI, ISO, AERB, FDA norms, and accessibility standards.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faProjectDiagram} /></div>
                    <h4>2. Optimized Functional Layout & Space Planning</h4>
                    <p>Facilitating smooth patient/staff flow, efficient resource access, and optimized workflows.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faLeaf} /></div>
                    <h4>3. Sustainability & Eco-Friendly Design</h4>
                    <p>Incorporating energy efficiency, sustainable materials, and green practices for responsibility.</p>
                </div>
              </div>
              {/* --- END NEW FEATURE CARDS --- */}
            </div>
          </div>
        </section>

        {/* --- REVIEW PROCESS (Detailed Planning Section - 5 items) --- */}
        <section className="cliniqor-section cliniqor-section-bg-texture cliniqor-detailed-planning-section"> {/* Kept existing classes */}
          <div className="cliniqor-container">
             <div ref={title3Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title3Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Cliniqor's Comprehensive Hospital Design Review Process</h2>
               <p>A detailed, systematic approach covering critical design elements.</p>
               {/* --- END NEW SECTION TITLE --- */}
            </div>
            {/* Using Planning Items for Review Elements */}
            <div className="cliniqor-planning-items-wrapper">
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faDraftingCompass} /></div>
                    <div className="cliniqor-content"><h4>1. Architectural Design Review</h4> <p>Assessing layout, structure, aesthetics, space efficiency, compliance, and future expansion potential.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faCogs} /></div>
                    <div className="cliniqor-content"><h4>2. MEP Design Review</h4> <p>Evaluating support for medical tech, power supply continuity, fire safety, and system efficiency.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faPalette} /></div>
                    <div className="cliniqor-content"><h4>3. Interior Design Review</h4> <p>Reviewing patient-centered features, ergonomics, strategic placement, and healing environment factors.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faDirections} /></div>
                    <div className="cliniqor-content"><h4>4. Wayfinding and Signage Review</h4> <p>Ensuring clarity, intuition, accessibility (ADA compliance), and ease of navigation.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faRulerCombined} /></div>
                    <div className="cliniqor-content"><h4>5. Structural Design Review</h4> <p>Verifying stability, safety, material durability, disaster resistance, and security measures.</p> </div>
                 </div>
                 {/* Only 5 items here */}
            </div>
          </div>
        </section>

        {/* --- ADVANCED FEATURES REVIEWED (Reusing Features Layout) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
           <div className="cliniqor-container">
              <div ref={title4Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title4Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Advanced Hospital Design Features Reviewed by Cliniqor</h2>
                 <p>Ensuring integration of crucial safety, technology, and adaptability elements.</p>
              </div>
              <div className="cliniqor-features-grid">
                    {/* --- Cards for Advanced Features --- */}
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faBiohazard} /></div>
                            <h4>6. Infection Control & Safety Protocols Review</h4>
                            <p>Assessing hygiene stations, negative pressure rooms, and cleanable materials.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faMicroscope} /></div>
                            <h4>7. Equipment Planning & Integration Review</h4>
                            <p>Evaluating infrastructure support, specialized spaces, and future tech readiness.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faExpandArrowsAlt} /></div>
                            <h4>8. Hospital Expansion & Adaptability Review</h4>
                            <p>Checking for modular designs, scalable systems, and flexible, re-purposable spaces.</p>
                        </div>
                    </div>
                    {/* Grid suited for 3 items */}
               </div>
           </div>
        </section>

       {/* --- CLINIQOR'S COMMITMENT (Simple Text Section) --- */}
        <section className="cliniqor-section"> {/* Default background */}
           <div className="cliniqor-container">
              <div ref={title5Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title5Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Cliniqor's Commitment to Your Hospital Design Success</h2>
                 <p>Empowering healthcare facilities to deliver excellence in patient care.</p>
              </div>
              <div className="cliniqor-text-content cliniqor-fade-in-up">
                   <p>Our comprehensive, expert-driven approach ensures your hospital will be compliant with all standards, maximize operational efficiency and patient satisfaction, and incorporate sustainability and future-ready technologies.</p>
                   <p>With Cliniqor, your design review is a seamless, efficient, and strategic process setting the foundation for long-term success. Our experienced professionals guide you through every stage, ensuring your vision becomes reality.</p>
              </div>
           </div>
        </section>

      </main>

      {/* --- FOOTER CTA --- */}
      <footer className="cliniqor-section cliniqor-section-narrow cliniqor-footer-cta">
        <div className="cliniqor-container cliniqor-fade-in-up">
          {/* --- UPDATED FOOTER CONTENT --- */}
          <h3>Ensure Your Hospital Design Meets the Highest Standards</h3>
          <p>Ready to create a future-ready, patient-centric, and compliant hospital? Contact Cliniqor today to begin your journey toward building a sustainable and efficient healthcare facility.</p>
          <a href="#contact" className="cliniqor-cta-button">Request a Design Review Consultation</a>
          {/* --- END UPDATED FOOTER CONTENT --- */}
        </div>
      </footer>
    </div> // End cliniqor-app-wrapper
  );
}

export default App;